import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "../layouts/AdminLayout";
import { Save, ArrowLeft, X, CheckCircle2, ChevronRight, Briefcase, User, FileText, CheckSquare, MapPin } from "lucide-react";
import api from "../../services/api";
import toast from "react-hot-toast";

const STEPS = [
  { id: 1, title: "Basic Info", icon: Briefcase },
  { id: 2, title: "Job Details", icon: CheckSquare },
  { id: 3, title: "Candidate Info", icon: User },
  { id: 4, title: "Job Description", icon: FileText },
  { id: 5, title: "Confirmation", icon: CheckCircle2 }
];

export default function AddJob() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [dbCategories, setDbCategories] = useState([]);
  
  // 🌟 NAYE STATES FOR API CITIES & LOCALITIES (Job Location)
  const [dbCities, setDbCities] = useState([]);
  const [dbLocalities, setDbLocalities] = useState([]);
  const [loadingLocalities, setLoadingLocalities] = useState(false);

  // 🌟 NAYE STATES FOR INTERVIEW LOCALITIES
  const [dbInterviewLocalities, setDbInterviewLocalities] = useState([]);
  const [loadingInterviewLocalities, setLoadingInterviewLocalities] = useState(false);

  const [currentStep, setCurrentStep] = useState(1);
  const [skillInput, setSkillInput] = useState("");

  const [formData, setFormData] = useState({
    title: "",
    category: "",
    vacancies: "1",
    job_type: "Full-Time", 
    contractual: false,
    work_location_type: "Work from office", 
    city: "",
    locality: "",
    deadline: "", 
    
    salary_min: "",
    salary_max: "",
    benefits: [], 
    shift: "Day", 
    working_days: "6 days working",
    interview_type: "In-person",
    interview_city: "",
    interview_locality: "",
    deposit_required: "No",

    gender: "Any", 
    qualification: "12th Pass", 
    exp_min: "0",
    exp_max: "1",
    freshers_only: false,
    industry: "ANY INDUSTRY",
    skills: [],
    english_fluency: "Understands Good English",

    description: "",
    status: "active",
  });

  const today = new Date().toISOString().split("T")[0]; 

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const token = localStorage.getItem("admin_token");
        const headers = { Authorization: `Bearer ${token}` };

        // 🌟 FETCH CATEGORIES AND CITIES TOGETHER
        const [catRes, cityRes] = await Promise.all([
          api.get("/categories", { headers }),
          api.get("/cities", { headers }).catch(() => ({ data: [] })) 
        ]);

        setDbCategories(catRes.data);
        setDbCities(cityRes.data || []);
      } catch (error) {
        console.error("Failed to fetch initial data:", error);
      }
    };
    fetchInitialData();
  }, []);

  // 🌟 JOB CITY CHANGE HONE PAR LOCALITIES FETCH KARNE KA LOGIC
  const handleCityChange = async (e) => {
    const selectedCity = e.target.value;
    setFormData({ ...formData, city: selectedCity, locality: "" }); 
    
    if (selectedCity) {
      setLoadingLocalities(true);
      try {
        const token = localStorage.getItem("admin_token");
        const response = await api.get(`/localities?city=${selectedCity}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setDbLocalities(response.data || []);
      } catch (error) {
        console.error("Error fetching localities:", error);
      } finally {
        setLoadingLocalities(false);
      }
    } else {
      setDbLocalities([]);
    }
  };

  // 🌟 INTERVIEW CITY CHANGE HONE PAR LOCALITIES FETCH KARNE KA LOGIC
  const handleInterviewCityChange = async (e) => {
    const selectedCity = e.target.value;
    setFormData({ ...formData, interview_city: selectedCity, interview_locality: "" }); 
    
    if (selectedCity) {
      setLoadingInterviewLocalities(true);
      try {
        const token = localStorage.getItem("admin_token");
        const response = await api.get(`/localities?city=${selectedCity}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setDbInterviewLocalities(response.data || []);
      } catch (error) {
        console.error("Error fetching interview localities:", error);
      } finally {
        setLoadingInterviewLocalities(false);
      }
    } else {
      setDbInterviewLocalities([]);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ ...formData, [name]: type === "checkbox" ? checked : value });
  };

  const handlePillSelect = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleMultiSelect = (field, value) => {
    const currentValues = formData[field];
    if (currentValues.includes(value)) {
      setFormData({ ...formData, [field]: currentValues.filter(v => v !== value) });
    } else {
      setFormData({ ...formData, [field]: [...currentValues, value] });
    }
  };

  const handleSkillKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const val = skillInput.trim();
      if (val && !formData.skills.includes(val)) {
        setFormData({ ...formData, skills: [...formData.skills, val] });
      }
      setSkillInput("");
    }
  };
  
  const removeSkill = (skillToRemove) => {
    setFormData({ ...formData, skills: formData.skills.filter(skill => skill !== skillToRemove) });
  };

  const validateStep = () => {
    if (currentStep === 1) {
      if (!formData.title) return toast.error("Job Title is required");
      if (!formData.category) return toast.error("Category is required");
      if (!formData.city) return toast.error("City is required");
      if (!formData.deadline) return toast.error("Application Deadline is required"); 
    }
    if (currentStep === 2) {
      if (!formData.salary_min || !formData.salary_max) return toast.error("Salary range is required");
    }
    if (currentStep === 3) {
      if (formData.skills.length === 0) return toast.error("Please add at least one skill");
    }
    if (currentStep === 4) {
      if (!formData.description) return toast.error("Job Description is required");
    }
    return true;
  };

  const handleNext = () => {
    if (validateStep()) setCurrentStep(prev => Math.min(prev + 1, 5));
  };

  const handlePrev = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validateStep()) return;
    setLoading(true);

    try {
      const token = localStorage.getItem("admin_token");
      
      const finalData = {
        ...formData,
        vacancies: parseInt(formData.vacancies),
        salary_range: `₹${formData.salary_min} - ₹${formData.salary_max}`,
        location: formData.locality ? `${formData.locality}, ${formData.city}` : formData.city,
        experience: formData.freshers_only ? "Fresher" : `${formData.exp_min}-${formData.exp_max} Years`,
      };

      await api.post("/jobs", finalData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success("Job posted successfully!");
      navigate("/jobs"); 
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || "Failed to post job.");
    } finally {
      setLoading(false);
    }
  };

  const Pill = ({ active, label, onClick }) => (
    <button
      type="button"
      onClick={onClick}
      className={`px-4 py-2 text-sm font-medium rounded-full border transition-all ${
        active 
          ? "bg-blue-50 border-blue-600 text-blue-700 shadow-sm" 
          : "bg-white border-gray-300 text-gray-700 hover:border-blue-300 hover:bg-blue-50/50"
      }`}
    >
      {label}
    </button>
  );

  return (
    <AdminLayout>
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10 -mx-6 px-6 pt-4 pb-0 mb-6">
        <div className="flex items-center justify-between pb-4">
          <button onClick={() => navigate(-1)} className="text-gray-500 hover:text-black">
            <ArrowLeft size={20} />
          </button>
          <div className="flex-1 max-w-4xl mx-auto flex items-center justify-between">
            {STEPS.map((step, idx) => (
              <div key={step.id} className="flex items-center">
                <div className={`flex flex-col items-center ${currentStep >= step.id ? 'text-blue-600' : 'text-gray-400'}`}>
                  <span className="text-xs font-semibold mb-1 uppercase tracking-wider">Step {step.id}</span>
                  <div className="flex items-center gap-2">
                    <step.icon size={18} className={currentStep === step.id ? 'text-blue-600' : (currentStep > step.id ? 'text-green-500' : 'text-gray-300')} />
                    <span className={`text-sm font-bold ${currentStep === step.id ? 'text-gray-900' : ''}`}>{step.title}</span>
                  </div>
                </div>
                {idx < STEPS.length - 1 && (
                  <div className={`w-16 sm:w-24 h-0.5 mx-4 mt-4 ${currentStep > step.id ? 'bg-blue-600' : 'bg-gray-200'}`}></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-6 items-start">
        
        <div className="flex-1 w-full bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
          
          {/* STEP 1: BASIC INFO */}
          {currentStep === 1 && (
            <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-300">
              <h2 className="text-xl font-bold text-gray-900 border-b pb-4">Basic Info</h2>
              
              <div className="space-y-6 max-w-2xl">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Job Title *</label>
                  <input type="text" name="title" value={formData.title} onChange={handleChange} placeholder="e.g. Customer Support Executive" className="w-full border border-gray-300 rounded-lg px-4 py-3 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500" />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Job Category *</label>
                    <select name="category" value={formData.category} onChange={handleChange} className="w-full border border-gray-300 rounded-lg px-4 py-3 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 bg-white">
                      <option value="" disabled>Select Category</option>
                      {dbCategories.map((cat) => (
                        <option key={cat.id} value={cat.name}>{cat.name}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Application Deadline *</label>
                    <input type="date" name="deadline" min={today} value={formData.deadline} onChange={handleChange} className="w-full border border-gray-300 rounded-lg px-4 py-3 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-gray-700" />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">Vacancy</label>
                  <div className="flex flex-wrap gap-3">
                    {["1", "2", "5", "10", "25", "50"].map(v => (
                      <Pill key={v} label={v} active={formData.vacancies === v} onClick={() => handlePillSelect('vacancies', v)} />
                    ))}
                    <input type="number" name="vacancies" value={formData.vacancies} onChange={handleChange} placeholder="Custom" className="w-24 border border-gray-300 rounded-full px-4 py-1.5 text-sm outline-none text-center focus:border-blue-500" />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">What type of job is this?</label>
                  <div className="flex gap-3">
                    {["Full-Time", "Part-Time", "Both"].map(type => (
                      <Pill key={type} label={type} active={formData.job_type === type} onClick={() => handlePillSelect('job_type', type)} />
                    ))}
                  </div>
                  <label className="flex items-center gap-2 mt-3 cursor-pointer text-sm text-gray-600">
                    <input type="checkbox" name="contractual" checked={formData.contractual} onChange={handleChange} className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                    This is a contractual job
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">Work location type</label>
                  <div className="flex gap-3 mb-2">
                    {["Work from office", "Field job", "Work from home"].map(loc => (
                      <Pill key={loc} label={loc} active={formData.work_location_type === loc} onClick={() => handlePillSelect('work_location_type', loc)} />
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Choose City *</label>
                    <select 
                      name="city" 
                      value={formData.city} 
                      onChange={handleCityChange} 
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 bg-white"
                    >
                      <option value="" disabled>Select City</option>
                      {dbCities.map((city, index) => (
                        <option key={index} value={city.name || city}>{city.name || city}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Choose Locality</label>
                    <select 
                      name="locality" 
                      value={formData.locality} 
                      onChange={handleChange} 
                      disabled={!formData.city || loadingLocalities}
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 bg-white disabled:bg-gray-100 disabled:text-gray-400"
                    >
                      <option value="" disabled>
                        {loadingLocalities ? "Loading localities..." : (!formData.city ? "Select a city first" : "Select Locality")}
                      </option>
                      {dbLocalities.map((loc, index) => (
                        <option key={index} value={loc.name || loc}>{loc.name || loc}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* STEP 2: JOB DETAILS */}
          {currentStep === 2 && (
            <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-300">
              <h2 className="text-xl font-bold text-gray-900 border-b pb-4">Job Details</h2>
              <div className="space-y-6 max-w-2xl">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Salary per month *</label>
                  <div className="flex items-center gap-4">
                    <div className="relative flex-1">
                      <span className="absolute left-3 top-3.5 text-gray-500">₹</span>
                      <input type="number" name="salary_min" value={formData.salary_min} onChange={handleChange} placeholder="Min" className="w-full border border-gray-300 rounded-lg pl-8 pr-4 py-3 outline-none focus:border-blue-500" />
                    </div>
                    <span className="text-gray-400 font-medium">To</span>
                    <div className="relative flex-1">
                      <span className="absolute left-3 top-3.5 text-gray-500">₹</span>
                      <input type="number" name="salary_max" value={formData.salary_max} onChange={handleChange} placeholder="Max" className="w-full border border-gray-300 rounded-lg pl-8 pr-4 py-3 outline-none focus:border-blue-500" />
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">Job Benefits (optional)</label>
                  <div className="flex flex-wrap gap-3">
                    {["Cab", "Meal", "Insurance", "PF", "Medical Benefits"].map(ben => (
                      <Pill key={ben} label={formData.benefits.includes(ben) ? `✓ ${ben}` : `+ ${ben}`} active={formData.benefits.includes(ben)} onClick={() => handleMultiSelect('benefits', ben)} />
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">Shifts</label>
                  <div className="flex gap-3">
                    {["Day", "Night", "Rotational", "Flexible"].map(s => (
                      <Pill key={s} label={s} active={formData.shift === s} onClick={() => handlePillSelect('shift', s)} />
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">Working Days</label>
                  <div className="flex gap-3">
                    {["5 days working", "6 days working"].map(wd => (
                      <Pill key={wd} label={wd} active={formData.working_days === wd} onClick={() => handlePillSelect('working_days', wd)} />
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">Interview Type</label>
                  <div className="flex gap-3">
                    {["In-person", "Telephonic"].map(it => (
                      <Pill key={it} label={it} active={formData.interview_type === it} onClick={() => handlePillSelect('interview_type', it)} />
                    ))}
                  </div>
                </div>

                {/* 🌟 INTERVIEW CITY & LOCALITY API DROPDOWNS YAHAN HAIN */}
                {formData.interview_type === "In-person" && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Interview City</label>
                      <select
                        name="interview_city"
                        value={formData.interview_city}
                        onChange={handleInterviewCityChange}
                        className="w-full border border-gray-300 rounded-lg px-4 py-3 outline-none focus:border-blue-500 bg-white"
                      >
                        <option value="" disabled>Select Interview City</option>
                        {dbCities.map((city, index) => (
                          <option key={index} value={city.name || city}>{city.name || city}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Interview Locality</label>
                      <select
                        name="interview_locality"
                        value={formData.interview_locality}
                        onChange={handleChange}
                        disabled={!formData.interview_city || loadingInterviewLocalities}
                        className="w-full border border-gray-300 rounded-lg px-4 py-3 outline-none focus:border-blue-500 bg-white disabled:bg-gray-100 disabled:text-gray-400"
                      >
                        <option value="" disabled>
                          {loadingInterviewLocalities ? "Loading..." : (!formData.interview_city ? "Select city first" : "Select Locality")}
                        </option>
                        {dbInterviewLocalities.map((loc, index) => (
                          <option key={index} value={loc.name || loc}>{loc.name || loc}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">Deposit Required?</label>
                  <div className="flex gap-3">
                    {["Yes", "No"].map(opt => (
                      <Pill key={opt} label={opt} active={formData.deposit_required === opt} onClick={() => handlePillSelect('deposit_required', opt)} />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* STEP 3: CANDIDATE INFO */}
          {currentStep === 3 && (
            <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-300">
              <h2 className="text-xl font-bold text-gray-900 border-b pb-4">Candidate Info</h2>
              <div className="space-y-6 max-w-2xl">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">Gender</label>
                  <div className="flex gap-3">
                    {["Any", "Male", "Female"].map(g => (
                      <Pill key={g} label={g} active={formData.gender === g} onClick={() => handlePillSelect('gender', g)} />
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">Qualification</label>
                  <div className="flex flex-wrap gap-3">
                    {["Any", "10th Pass", "12th Pass", "Diploma", "Graduate", "Post Graduate"].map(q => (
                      <Pill key={q} label={q} active={formData.qualification === q} onClick={() => handlePillSelect('qualification', q)} />
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">Required Experience</label>
                  <div className="flex items-center gap-4">
                    <select name="exp_min" value={formData.exp_min} onChange={handleChange} disabled={formData.freshers_only} className="flex-1 border border-gray-300 rounded-lg px-4 py-3 outline-none bg-white">
                      <option value="0">0 Years</option><option value="1">1 Year</option><option value="2">2 Years</option><option value="3">3+ Years</option>
                    </select>
                    <span className="text-gray-400 font-medium">to</span>
                    <select name="exp_max" value={formData.exp_max} onChange={handleChange} disabled={formData.freshers_only} className="flex-1 border border-gray-300 rounded-lg px-4 py-3 outline-none bg-white">
                      <option value="1">1 Year</option><option value="3">3 Years</option><option value="5">5 Years</option><option value="10">10+ Years</option>
                    </select>
                  </div>
                  <label className="flex items-center gap-2 mt-3 cursor-pointer text-sm text-gray-600">
                    <input type="checkbox" name="freshers_only" checked={formData.freshers_only} onChange={handleChange} className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                    Only freshers should apply
                  </label>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">Skills</label>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {formData.skills.map((skill, index) => (
                      <span key={index} className="flex items-center gap-1 bg-blue-50 text-blue-700 px-3 py-1.5 rounded-full text-sm font-medium border border-blue-200">
                        {skill} <button type="button" onClick={() => removeSkill(skill)} className="text-blue-500 hover:text-blue-800"><X size={14} /></button>
                      </span>
                    ))}
                  </div>
                  <input type="text" value={skillInput} onChange={(e) => setSkillInput(e.target.value)} onKeyDown={handleSkillKeyDown} placeholder="Press Enter to add..." className="w-full border border-gray-300 rounded-lg px-4 py-3 outline-none focus:border-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">English Fluency</label>
                  <div className="flex flex-wrap gap-3">
                    {["No English", "Understands basic English", "Understands Good English", "Speaks Fluent English"].map(ef => (
                      <Pill key={ef} label={ef} active={formData.english_fluency === ef} onClick={() => handlePillSelect('english_fluency', ef)} />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* STEP 4: JOB DESCRIPTION */}
          {currentStep === 4 && (
            <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-300">
              <h2 className="text-xl font-bold text-gray-900 border-b pb-4">Job Description</h2>
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Write detailed description, responsibilities, and requirements *</label>
                  <textarea name="description" value={formData.description} onChange={handleChange} rows={12} className="w-full border border-gray-300 rounded-lg px-4 py-3 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 leading-relaxed"></textarea>
                </div>
              </div>
            </div>
          )}

          {/* STEP 5: CONFIRMATION */}
          {currentStep === 5 && (
            <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-300">
              <h2 className="text-xl font-bold text-gray-900 border-b pb-4">Review & Confirm</h2>
              <div className="space-y-6">
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="font-bold text-lg text-gray-800">Job Details Summary</h3>
                    <button onClick={() => setCurrentStep(1)} className="text-blue-600 text-sm font-semibold hover:underline">Edit</button>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div><span className="text-gray-500 block">Title</span><span className="font-medium text-gray-900">{formData.title}</span></div>
                    <div><span className="text-gray-500 block">Category</span><span className="font-medium text-gray-900">{formData.category}</span></div>
                    <div><span className="text-gray-500 block">Location</span><span className="font-medium text-gray-900">{formData.locality ? `${formData.locality}, ${formData.city}` : formData.city}</span></div>
                    <div><span className="text-gray-500 block">Deadline</span><span className="font-medium text-gray-900">{formData.deadline}</span></div>
                    <div><span className="text-gray-500 block">Salary</span><span className="font-medium text-gray-900">₹{formData.salary_min} - ₹{formData.salary_max}</span></div>
                  </div>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="font-bold text-lg text-gray-800">Candidate Requirements</h3>
                    <button onClick={() => setCurrentStep(3)} className="text-blue-600 text-sm font-semibold hover:underline">Edit</button>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div><span className="text-gray-500 block">Experience</span><span className="font-medium text-gray-900">{formData.freshers_only ? "Fresher" : `${formData.exp_min}-${formData.exp_max} Years`}</span></div>
                    <div><span className="text-gray-500 block">Qualification</span><span className="font-medium text-gray-900">{formData.qualification}</span></div>
                    <div><span className="text-gray-500 block">Gender</span><span className="font-medium text-gray-900">{formData.gender}</span></div>
                    <div className="col-span-2"><span className="text-gray-500 block">Skills</span><span className="font-medium text-gray-900">{formData.skills.join(", ") || "None"}</span></div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* BOTTOM ACTION BAR */}
          <div className="mt-10 pt-6 border-t border-gray-100 flex items-center justify-between">
            <button 
              type="button" 
              onClick={handlePrev} 
              disabled={currentStep === 1}
              className="px-6 py-2.5 rounded-lg font-semibold text-gray-600 hover:bg-gray-100 disabled:opacity-30 transition-colors"
            >
              Back
            </button>
            {currentStep < 5 ? (
              <button type="button" onClick={handleNext} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-bold py-2.5 px-8 rounded-lg transition-colors shadow-sm">
                Save & Continue <ChevronRight size={18} />
              </button>
            ) : (
              <button type="button" onClick={handleSubmit} disabled={loading} className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white font-bold py-2.5 px-8 rounded-lg transition-colors shadow-sm disabled:opacity-50">
                {loading ? "Publishing..." : "Publish Job"} <Save size={18} />
              </button>
            )}
          </div>

        </div>

        {/* RIGHT SIDE: LIVE PREVIEW WIDGET */}
        <div className="hidden lg:block w-80 shrink-0">
          <div className="bg-blue-50/50 rounded-2xl border border-blue-100 shadow-sm sticky top-32 overflow-hidden">
            <div className="bg-white border-b border-blue-100 p-4">
              <h3 className="text-sm font-bold text-gray-800">Live Job Preview</h3>
            </div>
            <div className="p-5">
              <h1 className="text-lg font-bold text-gray-900 mb-1">{formData.title || "Job Title Here"}</h1>
              {(formData.salary_min || formData.salary_max) && (
                <p className="text-gray-700 font-semibold mb-3">₹{formData.salary_min || "Min"} - ₹{formData.salary_max || "Max"} / month</p>
              )}
              <div className="space-y-2 text-sm text-gray-600 mb-4">
                <div className="flex items-start gap-2"><MapPin size={16} className="shrink-0 mt-0.5" /><span>{formData.city ? (formData.locality ? `${formData.locality}, ${formData.city}` : formData.city) : "Location not set"}</span></div>
                <div className="flex items-start gap-2"><Briefcase size={16} className="shrink-0 mt-0.5" /><span>{formData.freshers_only ? "Fresher" : `${formData.exp_min} - ${formData.exp_max} years of experience`}</span></div>
              </div>
              <div className="bg-white rounded-xl border border-blue-100 p-3 shadow-sm mb-4">
                <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Job Highlights</h4>
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2 text-sm text-gray-700"><CheckCircle2 size={14} className="text-teal-500"/> {formData.qualification}</div>
                  <div className="flex items-center gap-2 text-sm text-gray-700"><CheckCircle2 size={14} className="text-teal-500"/> {formData.gender === "Any" ? "All Genders" : formData.gender}</div>
                  <div className="flex items-center gap-2 text-sm text-gray-700"><CheckCircle2 size={14} className="text-teal-500"/> {formData.shift} Shift</div>
                  {formData.benefits.length > 0 && (
                    <div className="flex items-start gap-2 text-sm text-gray-700"><CheckCircle2 size={14} className="text-teal-500 shrink-0 mt-0.5"/> <span className="line-clamp-2">{formData.benefits.join(", ")}</span></div>
                  )}
                </div>
              </div>
              {formData.skills.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Skills Required</h4>
                  <div className="flex flex-wrap gap-1">
                    {formData.skills.slice(0,3).map((sk,i) => <span key={i} className="text-[11px] bg-gray-100 text-gray-700 border px-2 py-0.5 rounded-full">{sk}</span>)}
                    {formData.skills.length > 3 && <span className="text-[11px] bg-gray-100 text-gray-700 border px-2 py-0.5 rounded-full">+{formData.skills.length - 3}</span>}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

      </div>
    </AdminLayout>
  );
}